/*
 * silwy-engraving-cart-check.js
 * ---------------------------------------------------------------------------
 * Sicherheitsnetz für Gravuren im Warenkorb (Ringo, 2026-10).
 *
 * Hintergrund: Gravurtext kam teilweise leer im Warenkorb/der Bestellung an
 * (Ursache: Hidden-Felder wurden nur auf "keyup" befüllt – Autokorrektur,
 * Wortvorschläge, Einfügen, Diktat lösen kein keyup aus; gefixt in
 * silwy-engraving.js). Dieses Skript fängt alle verbliebenen/alten Fälle ab:
 *
 *  - Gruppiert alle Cart-Positionen über die Property "_Gravur ID".
 *  - Eine Gruppe ist defekt, wenn eine Gravur-Position (Produkt-Handle
 *    "gravur") oder das Hauptprodukt weder Gravurtext noch Motiv enthält,
 *    oder eine Gravur-Position ohne zugehöriges Hauptprodukt existiert.
 *  - Defekte Gruppen: Gravur-Positionen werden entfernt, beim Hauptprodukt
 *    wird ohne Gravur-Properties neu in den Warenkorb gelegt (Produkt bleibt).
 *  - Danach Popup: Kunde soll die Gravur erneut eingeben.
 *
 * Läuft beim Seitenaufruf (nur wenn der Warenkorb nicht leer ist) und bei
 * jedem "cart:change"-Event des Themes.
 */
(() => {
  if (window.__silwyEngravingCartCheck) return;
  window.__silwyEngravingCartCheck = true;

  const cfg = window.silwyEngravingCartCheck || {};
  const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
  const ENGRAVING_HANDLE = cfg.engravingHandle || "gravur";

  // Property-Namen aller Sprachen (locales/*.json → silwy.engraving.property_1/_2)
  const TEXT_KEYS = new Set(
    ["Gravurtext", "Engraving text", "Texto grabado", "Gravurtext 1", "Gravurtext 2"].concat(cfg.textKeys || [])
  );
  const MOTIF_KEYS = new Set([
    "_Gravur Motiv", "_Gravur Motiv 1", "_Gravur Motiv 2",
    "Gravur Motiv Name", "Gravur Motiv Name 1", "Gravur Motiv Name 2",
  ]);
  const FONT_KEYS = new Set(
    [
      "Gravur Schriftart", "Engraving font", "Fuente de grabado", "Carattere di incisione",
      "Gravur Schriftart 1", "Gravur Schriftart 2",
    ].concat(cfg.fontKeys || [])
  );
  const ID_KEY = "_Gravur ID";

  const isEngravingKey = (k) => k === ID_KEY || TEXT_KEYS.has(k) || MOTIF_KEYS.has(k) || FONT_KEYS.has(k);
  const isEngravingLine = (item) =>
    item.handle === ENGRAVING_HANDLE || (item.product_title || "").trim().toLowerCase() === "gravur";
  const hasContent = (props) =>
    Object.entries(props || {}).some(
      ([k, v]) => (TEXT_KEYS.has(k) || MOTIF_KEYS.has(k)) && String(v ?? "").trim().length > 0
    );

  let running = false;

  function findBroken(cart) {
    const groups = new Map();
    const orphans = [];

    for (const item of cart.items || []) {
      const props = item.properties || {};
      const id = String(props[ID_KEY] ?? "").trim();
      if (!id) {
        // Gravur-Position ganz ohne Verknüpfung → kann nicht zugeordnet werden
        if (isEngravingLine(item)) orphans.push(item);
        continue;
      }
      if (!groups.has(id)) groups.set(id, []);
      groups.get(id).push(item);
    }

    const removeKeys = []; // Gravur-Positionen → Menge 0
    const stripLines = []; // Hauptprodukte → Gravur-Properties entfernen
    const products = []; // für das Popup

    orphans.forEach((o) => removeKeys.push(o.key));

    for (const items of groups.values()) {
      const engr = items.filter(isEngravingLine);
      const mains = items.filter((i) => !isEngravingLine(i));
      const broken =
        engr.some((i) => !hasContent(i.properties)) ||
        mains.some((i) => !hasContent(i.properties)) ||
        (engr.length > 0 && mains.length === 0);
      if (!broken) continue;

      engr.forEach((i) => removeKeys.push(i.key));
      mains.forEach((i) => {
        stripLines.push(i);
        products.push({ title: i.product_title, url: i.url });
      });
    }

    return { removeKeys, stripLines, products, hasWork: removeKeys.length + stripLines.length > 0 };
  }

  async function repair(result) {
    // 1) Gravur-Positionen UND betroffene Hauptprodukt-Positionen entfernen.
    //    (cart/change.js mit leerem properties-Objekt wird von Shopify ignoriert,
    //    deshalb Hauptprodukt entfernen und ohne Gravur-Properties neu hinzufügen.)
    const updates = {};
    result.removeKeys.forEach((k) => (updates[k] = 0));
    result.stripLines.forEach((l) => (updates[l.key] = 0));
    if (Object.keys(updates).length) {
      await fetch(`${root}cart/update.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      });
    }

    // 2) Hauptprodukte ohne Gravur wieder hinzufügen (übrige Properties bleiben erhalten)
    const items = result.stripLines.map((line) => {
      const clean = {};
      Object.entries(line.properties || {}).forEach(([k, v]) => {
        if (!isEngravingKey(k) && v !== null && v !== "") clean[k] = v;
      });
      const item = { id: line.variant_id, quantity: line.quantity };
      if (Object.keys(clean).length) item.properties = clean;
      if (line.selling_plan_allocation?.selling_plan?.id) item.selling_plan = line.selling_plan_allocation.selling_plan.id;
      return item;
    });
    if (items.length) {
      const res = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
        body: JSON.stringify({ items }),
      });
      if (!res.ok) console.error("[SilwyEngraving] Hauptprodukt konnte nicht neu hinzugefügt werden:", await res.text());
    }
  }

  function showPopup(products) {
    const t = cfg.texts || {};
    const dlg = document.createElement("dialog");
    dlg.className = "silwy-engraving-alert";
    const uniq = [];
    products.forEach((p) => {
      if (!uniq.some((u) => u.url === p.url)) uniq.push(p);
    });
    const list = uniq
      .map((p) => `<li><a href="${p.url}">${p.title}</a></li>`)
      .join("");
    dlg.innerHTML = `
      <h3 class="h5">${t.title || "Bitte Gravur erneut eingeben"}</h3>
      <p>${t.text || "Aufgrund eines technischen Problems konnte deine Gravur leider nicht übernommen werden. Wir haben die Gravur aus dem Warenkorb entfernt – der Artikel selbst ist weiterhin drin. Bitte gib deine Gravur beim Produkt noch einmal ein."}</p>
      ${list ? `<ul>${list}</ul>` : ""}
      <div class="silwy-engraving-alert__actions">
        ${uniq.length === 1 ? `<a class="button" href="${uniq[0].url}">${t.cta || "Gravur neu eingeben"}</a>` : ""}
        <button type="button" class="button button--secondary" data-close>${t.close || "Schließen"}</button>
      </div>`;
    document.body.appendChild(dlg);
    const close = () => {
      dlg.close();
      dlg.remove();
      if (window.themeVariables?.settings?.pageType === "cart") window.location.reload();
    };
    dlg.querySelector("[data-close]").addEventListener("click", close);
    dlg.addEventListener("cancel", (e) => {
      e.preventDefault();
      close();
    });
    if (typeof dlg.showModal === "function") dlg.showModal();
    else dlg.setAttribute("open", "");
  }

  async function check(cart) {
    if (running) return;
    running = true;
    try {
      if (!cart) cart = await (await fetch(`${root}cart.js`)).json();
      const result = findBroken(cart);
      if (!result.hasWork) return;

      console.warn("[SilwyEngraving] Defekte Gravur im Warenkorb gefunden – wird entfernt:", result);
      await repair(result);
      document.dispatchEvent(new CustomEvent("cart:refresh"));
      showPopup(result.products);
    } catch (e) {
      console.error("[SilwyEngraving] Cart-Check fehlgeschlagen:", e);
    } finally {
      running = false;
    }
  }

  document.addEventListener("cart:change", (e) => {
    // Eigene Reparatur löst cart:refresh aus, nicht cart:change → keine Schleife
    check(e.detail && e.detail.cart && e.detail.cart.items ? e.detail.cart : null);
  });

  if (cfg.cartHasItems) {
    if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", () => check());
    else check();
  }
})();
